import { useMutation, useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import api from "../api/axiosInstance";
import { ApiEndpoints } from "../api/endpoints";
import { authAtom } from "../store/auth";
import { APPS, CURRENT_APP, handoffUrl } from "../utils/apps";

const listOf = (res) => res?.data?.profiles ?? res?.profiles ?? [];

const useAppSwitcher = () => {
	const auth = useAtomValue(authAtom);
	const userId = auth?.user?.id ?? null;
	const signedIn = Boolean(auth?.access_token || Cookies.get("token"));

	const { data, isPending, isError } = useQuery({
		queryKey: ["user-profiles", userId],
		queryFn: async () => listOf(await api.get(ApiEndpoints.PROFILES)),
		enabled: signedIn,
		staleTime: 30 * 1000,
	});

	const profiles = Array.isArray(data) ? data : [];

	const apps = APPS.map((app) => ({
		...app,
		current: app.key === CURRENT_APP,
		profile: profiles.find((profile) => profile.app === app.key) ?? null,
	}));

	const handoff = useMutation({
		mutationFn: async ({ app }) => {
			const res = await api.post(ApiEndpoints.HANDOFF, { platform: app.profile?.platform || app.key });
			return res?.data?.code;
		},
		onSuccess: (code, { app, tab }) => {
			const url = handoffUrl(app, code);
			if (tab && !tab.closed) {
				tab.location.replace(url);
				return;
			}
			if (!window.open(url, "_blank", "noopener")) {
				toast.error(`Allow pop-ups for this site to open ${app.label}`);
			}
		},
		onError: (error, { app, tab }) => {
			tab?.close();
			toast.error(error?.message || `Could not open ${app.label}. Please try again.`);
		},
	});

	const openApp = (app) => {
		if (app.current || !app.profile) return;
		const tab = window.open("", "_blank");
		if (tab) tab.opener = null;
		handoff.mutate({ app, tab });
	};

	return {
		apps,
		loading: signedIn && isPending,
		failed: isError,
		openApp,
		opening: handoff.isPending ? handoff.variables?.app?.key : null,
	};
};

export default useAppSwitcher;
